import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { FramedImage } from "../components/ui/FramedImage";
import { useAuth } from "../App";
import { toast } from "sonner";
import { 
  Gavel, Clock, Users, Eye, EyeOff, ArrowLeft, 
  Calendar, Timer, TrendingUp, Lock, DollarSign,
  AlertCircle, CheckCircle2, Info, History
} from "lucide-react";
import { API } from "../apiConfig";


// Countdown Timer Component
const CountdownTimer = ({ endTime, onEnd, large = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const end = new Date(endTime).getTime();
      const now = new Date().getTime();
      const difference = end - now;

      if (difference <= 0) {
        onEnd?.();
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime, onEnd]);

  if (!timeLeft) {
    return <span className="text-destructive font-medium">Auction Ended</span>;
  }

  if (large) {
    return (
      <div className="flex gap-3">
        {timeLeft.days > 0 && (
          <div className="text-center">
            <div className="bg-primary/10 text-primary px-4 py-3 rounded-lg font-mono text-2xl font-bold">
              {timeLeft.days}
            </div>
            <span className="text-xs text-muted-foreground mt-1 block">Days</span>
          </div>
        )}
        <div className="text-center">
          <div className="bg-primary/10 text-primary px-4 py-3 rounded-lg font-mono text-2xl font-bold">
            {String(timeLeft.hours).padStart(2, '0')}
          </div>
          <span className="text-xs text-muted-foreground mt-1 block">Hours</span>
        </div>
        <div className="text-center">
          <div className="bg-primary/10 text-primary px-4 py-3 rounded-lg font-mono text-2xl font-bold">
            {String(timeLeft.minutes).padStart(2, '0')}
          </div>
          <span className="text-xs text-muted-foreground mt-1 block">Mins</span>
        </div>
        <div className="text-center">
          <div className="bg-primary/10 text-primary px-4 py-3 rounded-lg font-mono text-2xl font-bold">
            {String(timeLeft.seconds).padStart(2, '0')}
          </div>
          <span className="text-xs text-muted-foreground mt-1 block">Secs</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-sm">
      {timeLeft.days > 0 && (
        <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
          {timeLeft.days}d
        </span>
      )}
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.hours).padStart(2, '0')}h
      </span>
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.minutes).padStart(2, '0')}m
      </span>
      <span className="bg-primary/10 text-primary px-2 py-1 rounded font-mono">
        {String(timeLeft.seconds).padStart(2, '0')}s
      </span>
    </div>
  );
};

const AuctionDetailPage = () => {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [auction, setAuction] = useState(null);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bidAmount, setBidAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  // Collector status - user must be logged in to see auction details
  const isCollector = !!user;

  useEffect(() => {
    fetchAuction();
    if (user) {
      fetchMyBids();
    }
  }, [auctionId, user]);

  const fetchAuction = async () => {
    try {
      const response = await axios.get(`${API}/auctions/${auctionId}`);
      setAuction(response.data);
      
      // Set default bid amount
      const minBid = response.data.current_bid > 0 
        ? response.data.current_bid + response.data.min_bid_increment
        : response.data.starting_bid;
      setBidAmount(minBid.toString());
    } catch (error) {
      console.error("Error fetching auction:", error);
      toast.error("Auction not found");
      navigate("/auctions");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBids = async () => {
    try {
      const response = await axios.get(`${API}/auctions/${auctionId}/my-bids`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyBids(response.data);
    } catch (error) {
      console.error("Error fetching bids:", error);
    }
  };

  const handlePlaceBid = async () => {
    if (!user) {
      toast.error("Please sign in to place a bid");
      navigate("/login", { state: { from: `/auctions/${auctionId}` } });
      return;
    }

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid bid amount");
      return;
    }

    const minBid = auction.current_bid > 0 
      ? auction.current_bid + auction.min_bid_increment
      : auction.starting_bid;

    if (amount < minBid) {
      toast.error(`Minimum bid is $${minBid.toFixed(2)}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/auctions/${auctionId}/bid`,
        { amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      
      // Refresh auction data
      fetchAuction();
      fetchMyBids();

      // If auction was extended, show special message
      if (response.data.new_end_time) {
        toast.info("⏰ Auction extended by 5 minutes!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to place bid");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  const getMinBid = useCallback(() => {
    if (!auction) return 0;
    return auction.current_bid > 0 
      ? auction.current_bid + auction.min_bid_increment
      : auction.starting_bid;
  }, [auction]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-square bg-muted rounded-2xl" />
              <div className="space-y-4">
                <div className="h-8 bg-muted rounded w-1/4" />
                <div className="h-12 bg-muted rounded w-3/4" />
                <div className="h-24 bg-muted rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!auction) {
    return null;
  }

  const isActive = auction.status === "active" && auction.is_active;
  const isEnded = ["ended", "sold", "no_sale"].includes(auction.status);
  const isUpcoming = auction.status === "scheduled";
  const isHighestBidder = myBids.length > 0 && myBids[0].is_winning;

  return (
    <div className="min-h-screen" data-testid="auction-detail-page">
      <Navigation />

      <div className="pt-28 pb-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <Link 
            to="/auctions" 
            className="inline-flex items-center text-muted-foreground hover:text-foreground mb-8"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Auctions
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column - Images */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              {/* Main Image */}
              <div className="aspect-square bg-muted/30 rounded-2xl overflow-hidden mb-4 relative">
                <div className="absolute inset-0 flex items-center justify-center p-8">
                  <FramedImage
                    src={auction.product_images?.[selectedImage] || "/placeholder.jpg"}
                    alt={auction.product_title}
                    frameScale={0.7}
                  />
                </div>

                {/* Status Badge */}
                {isActive && (
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-green-500 text-white px-3 py-1.5 rounded-full">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                    </span>
                    <span className="text-sm font-medium">LIVE</span>
                  </div>
                )}

                {/* Visibility */}
                <div className="absolute top-4 right-4">
                  {auction.visibility === "silent" ? (
                    <div className="bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-white" />
                      <span className="text-white text-sm">Silent Auction</span>
                    </div>
                  ) : (
                    <div className="bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full flex items-center gap-2">
                      <Eye className="h-4 w-4" />
                      <span className="text-sm">Public Bids</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnail Gallery */}
              {auction.product_images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {auction.product_images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Right Column - Details & Bidding */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              {/* Category */}
              <Badge variant="outline" className="text-xs tracking-widest uppercase">
                {auction.category?.replace("_", " ")}
              </Badge>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-medium">
                {auction.title}
              </h1>

              {/* Description */}
              <p className="text-muted-foreground leading-relaxed">
                {auction.description}
              </p>

              <Separator />

              {/* Collector Gate Overlay */}
              {!isCollector && (
                <Card className="border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-transparent">
                  <CardContent className="py-8 text-center space-y-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <Lock className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-medium">Collector Access Required</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Sign in or create a free account to view auction details, current bids, and place your bid.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                      <Link to="/login">
                        <Button className="rounded-full px-8">
                          Sign In
                        </Button>
                      </Link>
                      <Link to="/register">
                        <Button variant="outline" className="rounded-full px-8">
                          Create Account
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Blurred Preview for Non-Collectors */}
              {!isCollector && (
                <div className="relative">
                  <div className="blur-md pointer-events-none select-none">
                    <Card className="border-2 border-border">
                      <CardHeader className="pb-4">
                        <CardTitle className="flex items-center gap-2">
                          <Gavel className="h-5 w-5" />
                          Place Your Bid
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="flex justify-between items-baseline">
                          <span className="text-muted-foreground">Current Bid</span>
                          <span className="text-4xl font-bold">$X,XXX</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span>XX bids</span>
                          <span>Min increment: $XXX</span>
                        </div>
                        <div className="bg-muted/50 rounded-xl p-4 h-20" />
                        <div className="h-14 bg-primary rounded-xl" />
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {/* Bidding Card - Only visible to collectors */}
              {isCollector && (
                <Card className={`border-2 ${isActive ? 'border-primary' : 'border-border'}`}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Gavel className="h-5 w-5 text-primary" />
                        {isActive ? "Place Your Bid" : isUpcoming ? "Auction Starting Soon" : "Auction Ended"}
                      </CardTitle>
                      {auction.reserve_met && auction.current_bid > 0 && (
                        <Badge className="bg-green-500 text-white">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Reserve Met
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Current/Starting Bid */}
                    <div className="flex justify-between items-baseline">
                      <span className="text-muted-foreground">
                        {auction.current_bid > 0 ? "Current Bid" : "Starting Bid"}
                      </span>
                      <span className="text-4xl font-bold text-primary">
                        {formatPrice(auction.current_bid > 0 ? auction.current_bid : auction.starting_bid)}
                      </span>
                    </div>

                    {/* Bid Count */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span>{auction.bid_count} bid{auction.bid_count !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        <span>Min increment: {formatPrice(auction.min_bid_increment)}</span>
                      </div>
                    </div>

                    {/* Countdown */}
                  {isActive && (
                    <div className="bg-muted/50 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Clock className="h-4 w-4" />
                        <span>Time Remaining</span>
                      </div>
                      <CountdownTimer endTime={auction.end_time} onEnd={fetchAuction} large />
                    </div>
                  )}

                  {isUpcoming && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 rounded-xl p-4">
                      <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 mb-2">
                        <Calendar className="h-4 w-4" />
                        <span>Auction Starts</span>
                      </div>
                      <p className="font-medium">{formatDate(auction.start_time)}</p>
                    </div>
                  )}

                  {/* Highest Bidder Notification */}
                  {isHighestBidder && isActive && (
                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-green-700 dark:text-green-400 font-medium">
                        You are the highest bidder!
                      </span>
                    </div>
                  )}

                  {/* Bid Input - Only show if active */}
                  {isActive && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="bid-amount">Your Bid (AUD)</Label>
                        <div className="relative mt-2">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            id="bid-amount"
                            type="number"
                            min={getMinBid()}
                            step={auction.min_bid_increment}
                            value={bidAmount}
                            onChange={(e) => setBidAmount(e.target.value)}
                            className="pl-10 text-lg h-14"
                            placeholder={`Min: ${formatPrice(getMinBid())}`}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Minimum bid: {formatPrice(getMinBid())}
                        </p>
                      </div>

                      <Button 
                        onClick={handlePlaceBid}
                        disabled={submitting}
                        className="w-full h-14 text-lg rounded-xl"
                        size="lg"
                      >
                        {submitting ? (
                          "Placing Bid..."
                        ) : (
                          <>
                            <Gavel className="mr-2 h-5 w-5" />
                            Place Bid
                          </>
                        )}
                      </Button>

                      {!user && (
                        <p className="text-sm text-center text-muted-foreground">
                          <Link to="/login" className="text-primary hover:underline">Sign in</Link>
                          {" "}to place a bid
                        </p>
                      )}
                    </div>
                  )}

                  {isEnded && (
                    <div className="text-center py-4">
                      <p className="text-lg font-medium mb-2">
                        {auction.status === "sold" ? "This auction has been sold" : 
                         auction.status === "no_sale" ? "Reserve price was not met" :
                         "This auction has ended"}
                      </p>
                      <Link to="/auctions">
                        <Button variant="outline">View Other Auctions</Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
              )}

              {/* My Bid History - Only for collectors */}
              {isCollector && myBids.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <History className="h-4 w-4" />
                      Your Bid History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {myBids.map((bid, idx) => (
                        <div 
                          key={bid.bid_id}
                          className={`flex justify-between items-center p-3 rounded-lg ${
                            bid.is_winning ? 'bg-green-50 dark:bg-green-950/30' : 'bg-muted/50'
                          }`}
                        >
                          <div>
                            <span className="font-medium">{formatPrice(bid.amount)}</span>
                            {bid.is_winning && (
                              <Badge className="ml-2 bg-green-500 text-white text-xs">
                                Winning
                              </Badge>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(bid.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Auction Info */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Auction ID</span>
                      <p className="font-mono">{auction.auction_id.slice(-8)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Category</span>
                      <p className="capitalize">{auction.category?.replace("_", " ")}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Start Time</span>
                      <p>{formatDate(auction.start_time)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">End Time</span>
                      <p>{formatDate(auction.end_time)}</p>
                    </div>
                  </div>

                  {auction.anti_sniping_enabled && (
                    <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
                      <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Anti-sniping protection: Bids placed in the last 5 minutes will extend the auction.
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* View Product Link */}
              <Link to={`/products/${auction.product_id}`}>
                <Button variant="outline" className="w-full">
                  View Full Product Details
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuctionDetailPage;
